import { useCallback } from "react";

import type { IRange } from "app/pages/sales-intelligence/types/range";
import { is } from "app/pages/sales-intelligence/utils/type-guards";

import type { IInternationalVisitsFilterDto } from "../../../types/leadsSearchFilters.dto";

interface IUseInternationalVisitsFilterParams {
    value: IInternationalVisitsFilterDto | undefined;
    onFilterUpdate: (value: IInternationalVisitsFilterDto) => void;
}

export const useInternationalVisitsFilter = ({ value, onFilterUpdate }: IUseInternationalVisitsFilterParams) => {
    const isCountrySelected = Boolean(value?.internationalCountry);

    const handleCountrySelect = useCallback(
        (country: { id: number } | null) => {
            if (country) {
                onFilterUpdate({ ...value, internationalCountry: country.id });
            }
        },
        [value, onFilterUpdate],
    );

    const handleVisitsClear = useCallback(
        (key: keyof IInternationalVisitsFilterDto) => () => {
            const updated = { ...value };
            delete updated[key];
            onFilterUpdate(updated);
        },
        [value, onFilterUpdate],
    );

    const handleVisitsChange = useCallback(
        (range: Partial<IRange<number>>) => {
            onFilterUpdate({ ...value, internationalVisits: range });
        },
        [value, onFilterUpdate],
    );

    const handleInternationalVisitsAfterChange = useCallback(
        (range: Partial<IRange<number>>) => {
            if (is.undefined(range.from) && is.undefined(range.to)) {
                handleVisitsClear("internationalVisitsShare")();
            } else {
                onFilterUpdate({ ...value, internationalVisitsShare: range });
            }
        },
        [value, onFilterUpdate, handleVisitsClear],
    );

    return {
        isCountrySelected,
        handleCountrySelect,
        handleVisitsClear,
        handleVisitsChange,
        handleInternationalVisitsAfterChange,
    };
};
